import { useEffect, useState } from 'react';

import { cn } from '../lib/utils';
import { subscribeRouteLoad } from './routeProgress';

/**
 * A thin bar across the very top of the screen while a lazy-loaded page
 * chunk is being fetched — masks the gap between clicking a nav link and
 * the `Suspense` fallback appearing, which is otherwise a blank instant.
 * Mostly matters on a page's *first* visit in a session; once its chunk is
 * cached by the browser, `import()` resolves fast enough that the bar
 * barely gets a chance to show, which is the right outcome, not a bug.
 */
export function RouteProgressBar() {
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => subscribeRouteLoad(setLoading), []);

  useEffect(() => {
    if (loading) {
      setVisible(true);
      return undefined;
    }
    if (!visible) return undefined;
    // Let the "done" fill-to-100%-then-fade animation finish before
    // unmounting, instead of snapping the bar away mid-transition.
    const id = window.setTimeout(() => setVisible(false), 350);
    return () => window.clearTimeout(id);
  }, [loading, visible]);

  if (!visible) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[70] h-0.5 overflow-hidden"
    >
      <div
        className={cn(
          'h-full bg-primary shadow-[0_0_8px_var(--primary)]',
          loading ? 'route-progress-fill' : 'route-progress-done',
        )}
      />
    </div>
  );
}
